import { CELL, GROUND } from '../core/config';
import type { City } from './city';

export const WATER_LEVEL = 0.061;
export interface Terrain {
  vertices: Float32Array;
  indices: Uint32Array;
  rows: number[];
  columns: number;
  extent: number;
  coast?: { beachStart: number; shore: number; bed: number };
  naturalBoundary?: boolean;
}
const cache = new WeakMap<City, Terrain>();
const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));
const smooth = (a: number, b: number, n: number) => {
  const t = clamp((n - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
};

/** Ridges occupy the previously blank rim. The city and airport stay on their
 * existing ground; a low east-west valley preserves the runway flight corridor. */
function boundaryHeight(x: number, z: number) {
  const west = smooth(675 + 22 * Math.sin(z * 0.008), 910, -x),
    north = smooth(675 + 22 * Math.cos(x * 0.007), 940, -z),
    east = smooth(700 + 22 * Math.sin(z * 0.01), 980, x) * smooth(-340, -180, z),
    ridge = Math.max(west, north, east),
    flightValley = smooth(70, 170, Math.abs(z + 580));
  const peaks = 0.6 + 0.23 * Math.sin(x * 0.013 + z * 0.006) + 0.17 * Math.cos(z * 0.02 - x * 0.009);
  const upland = GROUND + ridge * ridge * 230 * peaks * flightValley;
  const outline =
    Math.pow(Math.abs(x - 60) / 1100, 2.8) +
    Math.pow(Math.abs(z + 110) / 1000, 2.8) +
    0.08 * Math.sin(x * 0.007 + z * 0.004) +
    0.045 * Math.sin(z * 0.009 - x * 0.003);
  // Retain the existing runway as a low peninsula with a clear flight valley.
  const airport =
    smooth(310, 410, x) * (1 - smooth(1165, 1180, x)) * smooth(-690, -635, z) * (1 - smooth(-320, -260, z));
  const edge = Math.max(
    smooth(0.92, 1.07, outline) * (1 - airport),
    smooth(1155, 1180, Math.max(Math.abs(x), Math.abs(z))),
  );
  return upland * (1 - edge) - 8 * edge;
}

/** One modest strip mesh, shared verbatim by the rendered surface and collider.
 * River banks, finite end caps and the coastal slope are actual occupied ground. */
export function terrainFor(city: City): Terrain {
  const saved = cache.get(city);
  if (saved) return saved;
  const extent = city.half + 8,
    river = city.river,
    naturalBoundary = !!city.regional && city.half >= 1100,
    parcels = city.strategic?.parcels ?? [],
    parcelXs = parcels.flatMap((p) => [
      ...[-160, -100, -40, 0, 20].map((offset) => p.minX + offset),
      ...[-20, 0, 40, 100, 160].map((offset) => p.maxX + offset),
    ]),
    westSamples = naturalBoundary
      ? [
          ...new Set([
            -1110,
            -1030,
            -960,
            -900,
            -825,
            -750,
            -685,
            -645,
            ...parcelXs.filter((x) => x <= -645 && x > -extent),
          ]),
        ].sort((a, b) => a - b)
      : [],
    eastSamples = naturalBoundary
      ? [
          ...new Set([
            630,
            680,
            740,
            820,
            900,
            975,
            1040,
            1100,
            1145,
            1165,
            ...parcelXs.filter((x) => x >= 630 && x < extent),
          ]),
        ].sort((a, b) => a - b)
      : [],
    coast = city.regional ? { beachStart: 630, shore: 668, bed: -8 } : undefined,
    rows = [
      ...new Set(
        [
          -extent,
          ...(naturalBoundary
            ? [-1120, -1050, -990, -930, -860, -790, -720, -675, -645, 810, 880, 960, 1040, 1120]
            : []),
          ...(river?.points.flatMap((p, i, all) => [
            ...(i === 0 ? [p.z - 6] : []),
            p.z,
            ...(i === all.length - 1 ? [p.z + 6] : []),
          ]) ?? []),
          ...(coast ? [610, 630, 645, 660, 668, 672, 692, 716, 742] : []),
          ...parcels.flatMap((p) => [
            ...[-160, -100, -40, 0, 20].map((offset) => p.minZ + offset),
            ...[-20, 0, 40, 100, 160].map((offset) => p.maxZ + offset),
          ]),
          extent,
        ].filter((z) => z >= -extent && z <= extent),
      ),
    ].sort((a, b) => a - b),
    inlandSamples = naturalBoundary && river ? 8 : 0,
    columns = (river ? 8 : 2) + westSamples.length + eastSamples.length + inlandSamples * 2,
    vertices: number[] = [],
    indices: number[] = [];
  const center = (z: number) => {
    if (!river?.points.length) return 0;
    if (river.points.length === 1) return river.points[0].x;
    let i = 1;
    while (i < river.points.length - 1 && z > river.points[i].z) i++;
    const a = river.points[i - 1],
      b = river.points[i] ?? a,
      t = clamp((z - a.z) / Math.max(0.001, b.z - a.z), 0, 1);
    return a.x + (b.x - a.x) * t;
  };
  for (const z of rows) {
    const cx = center(z),
      w = (river?.width ?? 0) / 2,
      endWeight = river
        ? clamp(Math.min(z - river.points[0].z + 6, river.points.at(-1)!.z + 6 - z) / 6, 0, 1)
        : 0,
      coastalY = coast && z > coast.shore ? Math.max(coast.bed, GROUND - (z - coast.shore) * 0.11) : Infinity,
      // Sample both sides of the river across the northern ridge. Previously
      // two 600m-wide triangles stretched each mountain row into a flat slab.
      westInland = Array.from(
        { length: inlandSamples },
        (_, i) => -645 + ((cx - w - 3.5 + 645) * (i + 1)) / (inlandSamples + 1),
      ),
      eastInland = Array.from(
        { length: inlandSamples },
        (_, i) => cx + w + 3.5 + ((630 - cx - w - 3.5) * (i + 1)) / (inlandSamples + 1),
      ),
      xs = river
        ? [
            -extent,
            ...westSamples,
            ...westInland,
            cx - w - 3.5,
            cx - w,
            cx - w + Math.min(4, w * 0.65),
            cx + w - Math.min(4, w * 0.65),
            cx + w,
            cx + w + 3.5,
            ...eastInland,
            ...eastSamples,
            extent,
          ]
        : [-extent, ...westSamples, ...eastSamples, extent],
      depth = river
        ? [
            GROUND,
            ...westSamples.map(() => GROUND),
            ...westInland.map(() => GROUND),
            GROUND,
            -0.3,
            -6.5,
            -6.5,
            -0.3,
            GROUND,
            ...eastInland.map(() => GROUND),
            ...eastSamples.map(() => GROUND),
            GROUND,
          ]
        : xs.map(() => GROUND);
    xs.forEach((x, i) => {
      const riverY = GROUND + (depth[i] - GROUND) * endWeight;
      let ground = naturalBoundary ? (riverY < GROUND ? riverY : boundaryHeight(x, z)) : riverY;
      // Campus grading cuts only dry upland. The sea and the surrounding ridge
      // remain the same physical surface; no water is filled to fake land share.
      if (ground > GROUND)
        for (const p of parcels) {
          const inset = Math.min(x - p.minX, p.maxX - x, z - p.minZ, p.maxZ - z);
          // A broad earth slope meets the surrounding ridge. The previous
          // twenty-metre cut formed an implausible vertical ribbon at fences.
          if (inset > -140) ground += (GROUND - ground) * smooth(-140, 20, inset);
        }
      vertices.push(x, Math.min(coastalY, ground), z);
    });
  }
  for (let r = 0; r < rows.length - 1; r++)
    for (let i = 0; i < columns - 1; i++) {
      const a = r * columns + i,
        b = a + columns,
        c = b + 1,
        d = a + 1;
      indices.push(a, b, d, b, c, d);
    }
  const terrain = {
    vertices: new Float32Array(vertices),
    indices: new Uint32Array(indices),
    rows,
    columns,
    extent,
    coast,
    naturalBoundary,
  };
  cache.set(city, terrain);
  return terrain;
}

/** Height on the exact stored triangle, not a second approximate height function. */
export function terrainHeight(terrain: Terrain, x: number, z: number): number | undefined {
  if (Math.abs(x) > terrain.extent || Math.abs(z) > terrain.extent) return;
  let lo = 0,
    hi = terrain.rows.length - 2;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (terrain.rows[mid + 1] < z) lo = mid + 1;
    else hi = mid;
  }
  const p = terrain.vertices,
    n = terrain.columns;
  const height = (A: number, B: number, C: number) => {
    const den = (p[B + 2] - p[C + 2]) * (p[A] - p[C]) + (p[C] - p[B]) * (p[A + 2] - p[C + 2]);
    const u = ((p[B + 2] - p[C + 2]) * (x - p[C]) + (p[C] - p[B]) * (z - p[C + 2])) / den,
      v = ((p[C + 2] - p[A + 2]) * (x - p[C]) + (p[A] - p[C]) * (z - p[C + 2])) / den;
    if (u >= -1e-6 && v >= -1e-6 && u + v <= 1 + 1e-6)
      return u * p[A + 1] + v * p[B + 1] + (1 - u - v) * p[C + 1];
  };
  const t = (z - p[lo * n * 3 + 2]) / (p[(lo + 1) * n * 3 + 2] - p[lo * n * 3 + 2]);
  for (let i = 0; i < n - 1; i++) {
    const a = (lo * n + i) * 3,
      b = a + n * 3,
      c = b + 3,
      d = a + 3;
    if (x < p[a] + (p[b] - p[a]) * t - 1e-6 || x > p[d] + (p[c] - p[d]) * t + 1e-6) continue;
    return height(a, b, d) ?? height(b, c, d);
  }
}

/** Conservative rectangle inside one exactly flat authored terrain quad.
 * Sloping or skewed edge portions deliberately retain ordinary ray/sweep
 * movement. No sampling approximation is used to certify a flat surface. */
export function flatTerrainPatch(
  terrain: Terrain,
  x: number,
  z: number,
): { minX: number; maxX: number; minZ: number; maxZ: number; y: number } | undefined {
  if (Math.abs(x) > terrain.extent || Math.abs(z) > terrain.extent) return;
  let lo = 0,
    hi = terrain.rows.length - 2;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (terrain.rows[mid + 1] < z) lo = mid + 1;
    else hi = mid;
  }
  const p = terrain.vertices,
    n = terrain.columns;
  for (let i = 0; i < n - 1; i++) {
    const a = (lo * n + i) * 3,
      b = a + n * 3,
      c = b + 3,
      d = a + 3;
    const minX = Math.max(p[a], p[b]),
      maxX = Math.min(p[d], p[c]);
    if (x < minX || x > maxX || p[a + 1] !== p[b + 1] || p[a + 1] !== p[c + 1] || p[a + 1] !== p[d + 1])
      continue;
    return { minX, maxX, minZ: p[a + 2], maxZ: p[b + 2], y: p[a + 1] };
  }
}

/** Positive margin is dry-foot clearance from actual wet ground. */
export function wetGround(city: City, x: number, z: number, margin = 0): boolean {
  const t = city.terrain;
  if (!t) return false;
  for (const [dx, dz] of [
    [0, 0],
    [margin, 0],
    [-margin, 0],
    [0, margin],
    [0, -margin],
  ])
    if ((terrainHeight(t, x + dx, z + dz) ?? GROUND) < WATER_LEVEL - 0.005) return true;
  return false;
}

/** Author before rendering/physics. Existing bridge root IDs extend down to the
 * bed instead of introducing immortal visual pilings or unsupported foundations. */
export function prepareTerrain(city: City): City {
  if (city.terrain) return city;
  if (city.regional && city.river) {
    const last = city.river.points.at(-1)!;
    for (let z = last.z + 15; z <= 760; z += 15) city.river.points.push({ x: last.x, z });
  }
  const terrain = (city.terrain = terrainFor(city));
  if (city.strategic) {
    const fences = new Set(city.strategic.fences),
      omitted = new Set<number>();
    city.props = city.props?.filter((b) => {
      if (!fences.has(b.id)) return true;
      const roots = b.cells.filter((c) => c.root),
        heights = roots.map(
          (c) => terrainHeight(terrain, b.x + (c.x + 0.5) * CELL, b.z + (c.z + 0.5) * CELL) ?? -8,
        );
      // The natural waterfront closes the boundary where no dry fence footing
      // exists. Never leave an authored panel hovering over open water.
      if (heights.some((y) => y < WATER_LEVEL)) {
        omitted.add(b.id);
        return false;
      }
      b.baseY = Math.min(...heights);
      for (let i = 0; i < roots.length; i++) {
        const c = roots[i],
          bounds = c.bounds!;
        const lift = (heights[i] - b.baseY) / CELL;
        c.bounds = { ...bounds, y: lift, h: Math.max(0.05, 1 - lift) };
      }
      return true;
    });
    city.strategic.fences = city.strategic.fences.filter((id) => !omitted.has(id));
    for (const parcel of city.strategic.parcels)
      parcel.buildings = parcel.buildings.filter((id) => !omitted.has(id));
  }
  for (const tree of city.props ?? []) {
    if (tree.kind !== 'tree' || tree.parentCell !== undefined) continue;
    const roots = tree.cells.filter((c) => c.y === 0),
      grounded = (x: number, z: number) =>
        roots.every(
          (c) =>
            (terrainHeight(terrain, x + (c.x + 0.5) * CELL, z + (c.z + 0.5) * CELL) ?? GROUND) >=
            GROUND - 0.002,
        );
    if (grounded(tree.x, tree.z)) continue;
    const clear = (x: number, z: number) => {
      if (!grounded(x, z)) return false;
      for (const c of roots) {
        const px = x + (c.x + 0.5) * CELL,
          pz = z + (c.z + 0.5) * CELL;
        if (
          city.buildings.some(
            (b) =>
              px > b.x - 0.6 && px < b.x + b.w * CELL + 0.6 && pz > b.z - 0.6 && pz < b.z + b.d * CELL + 0.6,
          )
        )
          return false;
        for (const edge of city.roadGraph?.edges ?? [])
          for (let i = 1; i < (edge.profile?.length ?? 0); i++) {
            const a = edge.profile![i - 1],
              b = edge.profile![i],
              dx = b.x - a.x,
              dz = b.z - a.z,
              t = clamp(((px - a.x) * dx + (pz - a.z) * dz) / (dx * dx + dz * dz), 0, 1);
            if (
              a.y + (b.y - a.y) * t < 1 &&
              Math.hypot(px - a.x - dx * t, pz - a.z - dz * t) < edge.width / 2 + 1.7
            )
              return false;
          }
      }
      return true;
    };
    let placed = false;
    for (let r = 0.5; r <= 24 && !placed; r += 0.5)
      for (const [dx, dz] of [
        [-r, 0],
        [r, 0],
        [0, -r],
        [0, r],
      ])
        if (clear(tree.x + dx, tree.z + dz)) {
          tree.x += dx;
          tree.z += dz;
          placed = true;
          break;
        }
  }
  for (const building of [...(city.bridges ?? []), ...(city.infrastructure ?? [])]) {
    if (building.kind !== 'bridge') continue;
    const q = building.rotation ?? { x: 0, y: 0, z: 0, w: 1 },
      cos = 1 - 2 * q.y * q.y,
      sin = 2 * q.y * q.w;
    for (const cell of building.cells) {
      if (!(cell.root ?? cell.y === 0) || cell.role !== 'column') continue;
      const lx = (cell.x + 0.5) * CELL,
        lz = (cell.z + 0.5) * CELL,
        wx = building.x + cos * lx + sin * lz,
        wz = building.z - sin * lx + cos * lz,
        ground = Math.min(
          ...[-CELL / 2, CELL / 2].flatMap((dx) =>
            [-CELL / 2, CELL / 2].map((dz) => terrainHeight(terrain, wx + dx, wz + dz) ?? GROUND),
          ),
        ),
        base = building.baseY ?? GROUND;
      if (ground >= base - 0.02) continue;
      const prior = cell.bounds ?? { x: 0, y: 0, z: 0, w: 1, h: 1, d: 1 },
        low = (ground - base) / CELL - cell.y - 0.025;
      cell.bounds = { ...prior, y: low, h: prior.y + prior.h - low };
    }
  }
  return city;
}
